/**
 * Staff — society payroll module.
 *
 * Three tabs:
 *   1. Staff       — roster (create / edit / mark attendance today / terminate)
 *   2. Attendance  — daily attendance log, bulk-mark-present, monthly view, CSV export
 *   3. Payroll     — monthly payroll generation, mark-paid, CSV export
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  HardHat,
  Plus,
  Edit3,
  Power,
  Filter,
  CalendarDays,
  CheckCircle2,
  Download,
  IndianRupee,
  Users as UsersIcon,
  Coffee,
  MoreHorizontal,
  PlayCircle,
  Receipt as ReceiptIcon,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { get, post, patch, del, getErrorMessage, api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDate, formatDateTime, todayIsoDate } from '@/lib/format';

// ───── types ─────

type StaffCategory =
  | 'housekeeping'
  | 'gardening'
  | 'security_assistant'
  | 'maintenance'
  | 'reception'
  | 'driver'
  | 'cook'
  | 'electrician_inhouse'
  | 'plumber_inhouse'
  | 'carpenter_inhouse'
  | 'manager'
  | 'other';

type EmploymentType = 'full_time' | 'part_time' | 'contract';
type StaffStatus = 'active' | 'on_leave' | 'inactive' | 'terminated';
type AttendanceStatus = 'present' | 'absent' | 'half_day' | 'leave' | 'holiday' | 'overtime';
type PayrollStatus = 'pending' | 'paid' | 'cancelled';
type PaidVia = 'bank_transfer' | 'upi' | 'cash' | 'cheque';
type IdProofType = 'aadhaar' | 'pan' | 'driving_licence' | 'passport' | 'other';

interface Staff {
  id: string;
  full_name: string;
  phone: string;
  email: string | null;
  category: StaffCategory;
  employment_type: EmploymentType;
  monthly_salary_inr: number;
  joining_date: string;
  exit_date: string | null;
  exit_reason: string | null;
  status: StaffStatus;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  address: string | null;
  notes: string | null;
  bank_account_last4: string | null;
  pan_last4: string | null;
  photo_url: string | null;
  photo_storage_key: string | null;
  id_proof_type: IdProofType | null;
  id_proof_number_last4: string | null;
  id_proof_url: string | null;
  id_proof_storage_key: string | null;
  created_at: string;
  updated_at: string;
}

const ID_PROOF_LABEL: Record<IdProofType, string> = {
  aadhaar: 'Aadhaar',
  pan: 'PAN',
  driving_licence: 'Driving licence',
  passport: 'Passport',
  other: 'Other'
};

const ID_PROOF_OPTIONS: IdProofType[] = ['aadhaar', 'pan', 'driving_licence', 'passport', 'other'];

const STAFF_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp'];
const STAFF_IMAGE_ACCEPT = STAFF_IMAGE_MIME.join(',');

interface AttendanceRow {
  id: string;
  staff_id: string;
  date_local: string;
  status: AttendanceStatus;
  check_in_at: string | null;
  check_out_at: string | null;
  hours_worked: number | null;
  notes: string | null;
  marked_by_id: string | null;
}

interface PayrollRow {
  id: string;
  staff_id: string;
  period_label: string;
  base_salary_inr: number;
  attendance_days_worked: number;
  attendance_total_days: number;
  deductions_inr: number;
  bonus_inr: number;
  net_pay_inr: number;
  status: PayrollStatus;
  paid_at: string | null;
  paid_via: string | null;
  payment_reference: string | null;
  notes: string | null;
  staff?: { id: string; full_name: string; category: StaffCategory; phone: string };
}

// ───── labels ─────

const CATEGORY_LABEL: Record<StaffCategory, string> = {
  housekeeping: 'Housekeeping',
  gardening: 'Gardening',
  security_assistant: 'Security assistant',
  maintenance: 'Maintenance',
  reception: 'Reception',
  driver: 'Driver',
  cook: 'Cook',
  electrician_inhouse: 'Electrician (in-house)',
  plumber_inhouse: 'Plumber (in-house)',
  carpenter_inhouse: 'Carpenter (in-house)',
  manager: 'Manager',
  other: 'Other'
};

const CATEGORY_OPTIONS: StaffCategory[] = [
  'housekeeping',
  'gardening',
  'security_assistant',
  'maintenance',
  'reception',
  'driver',
  'cook',
  'electrician_inhouse',
  'plumber_inhouse',
  'carpenter_inhouse',
  'manager',
  'other'
];

const EMPLOYMENT_LABEL: Record<EmploymentType, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
  contract: 'Contract'
};

const STATUS_LABEL: Record<StaffStatus, string> = {
  active: 'Active',
  on_leave: 'On leave',
  inactive: 'Inactive',
  terminated: 'Terminated'
};

const STATUS_VARIANT: Record<StaffStatus, 'ok' | 'warn' | 'muted' | 'alert'> = {
  active: 'ok',
  on_leave: 'warn',
  inactive: 'muted',
  terminated: 'alert'
};

const ATTENDANCE_LABEL: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  half_day: 'Half day',
  leave: 'Leave',
  holiday: 'Holiday',
  overtime: 'Overtime'
};

const ATTENDANCE_VARIANT: Record<AttendanceStatus, 'ok' | 'alert' | 'warn' | 'info' | 'muted'> = {
  present: 'ok',
  absent: 'alert',
  half_day: 'warn',
  leave: 'info',
  holiday: 'muted',
  overtime: 'ok'
};

const PAYROLL_STATUS_LABEL: Record<PayrollStatus, string> = {
  pending: 'Pending',
  paid: 'Paid',
  cancelled: 'Cancelled'
};

const PAYROLL_STATUS_VARIANT: Record<PayrollStatus, 'warn' | 'ok' | 'muted'> = {
  pending: 'warn',
  paid: 'ok',
  cancelled: 'muted'
};

// ───── helpers ─────

function formatInr(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function currentMonthLabel(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('');
}

/**
 * Fetches an authenticated staff blob (photo or id-proof) and returns a
 * temporary object URL. Re-fetches when the staff row's storage key changes
 * (so a fresh upload busts the preview cache) and revokes on unmount.
 */
function useStaffBlobUrl(staffId: string | null | undefined, kind: 'photo' | 'id-proof', cacheBust: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!staffId || !cacheBust) {
      setUrl(null);
      return;
    }
    let revoked = false;
    let createdUrl: string | null = null;
    api
      .get<Blob>(`/staff/${staffId}/${kind}`, { responseType: 'blob' })
      .then((res) => {
        if (revoked) return;
        createdUrl = URL.createObjectURL(res.data);
        setUrl(createdUrl);
      })
      .catch(() => {
        if (!revoked) setUrl(null);
      });
    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [staffId, kind, cacheBust]);
  return url;
}

/**
 * StaffAvatar
 *
 * `variant="circle"` (default) — circular small avatar used in tables (32px).
 * `variant="passport"` — passport-style (3.5:4.5) used in detail/edit views.
 *   For passport variant `size` controls the WIDTH; height = width × (170/140).
 */
function StaffAvatar({
  staff,
  size = 32,
  variant = 'circle'
}: {
  staff: Staff;
  size?: number;
  variant?: 'circle' | 'passport';
}): JSX.Element {
  const photoUrl = useStaffBlobUrl(staff.id, 'photo', staff.photo_storage_key);
  const isPassport = variant === 'passport';
  const width = size;
  const height = isPassport ? Math.round(size * (170 / 140)) : size;
  const shapeCls = isPassport
    ? 'rounded-vt-md border border-vt-border'
    : 'rounded-full';
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={staff.full_name}
        className={`shrink-0 object-cover ${shapeCls}`}
        style={{ width, height }}
      />
    );
  }
  return (
    <span
      className={`flex shrink-0 items-center justify-center bg-vt-primary-100 text-vt-primary font-semibold ${shapeCls} ${isPassport ? 'text-vt-24' : 'text-vt-12'}`}
      style={{ width, height }}
    >
      {initialsFor(staff.full_name)}
    </span>
  );
}

// ───── form schemas ─────

const StaffFormSchema = z.object({
  full_name: z.string().trim().min(2, 'Name required').max(120),
  phone: z.string().trim().min(7, 'Phone required').max(20),
  email: z.string().trim().email().max(160).optional().or(z.literal('')),
  category: z.enum([
    'housekeeping',
    'gardening',
    'security_assistant',
    'maintenance',
    'reception',
    'driver',
    'cook',
    'electrician_inhouse',
    'plumber_inhouse',
    'carpenter_inhouse',
    'manager',
    'other'
  ]),
  employment_type: z.enum(['full_time', 'part_time', 'contract']),
  monthly_salary_inr: z.coerce.number().int().min(0).max(10_000_000),
  joining_date: z.string().min(1, 'Joining date required'),
  emergency_contact_name: z.string().trim().max(120).optional().or(z.literal('')),
  emergency_contact_phone: z.string().trim().max(20).optional().or(z.literal('')),
  address: z.string().trim().max(500).optional().or(z.literal('')),
  bank_account_last4: z.string().trim().max(10).optional().or(z.literal('')),
  pan_last4: z.string().trim().max(10).optional().or(z.literal('')),
  notes: z.string().trim().max(1000).optional().or(z.literal('')),
  id_proof_type: z
    .enum(['aadhaar', 'pan', 'driving_licence', 'passport', 'other'])
    .optional()
    .or(z.literal('')),
  id_proof_number_last4: z.string().trim().max(10).optional().or(z.literal(''))
});
type StaffFormValues = z.infer<typeof StaffFormSchema>;

const DEFAULT_STAFF_FORM: StaffFormValues = {
  full_name: '',
  phone: '',
  email: '',
  category: 'housekeeping',
  employment_type: 'full_time',
  monthly_salary_inr: 0,
  joining_date: todayIsoDate(),
  emergency_contact_name: '',
  emergency_contact_phone: '',
  address: '',
  bank_account_last4: '',
  pan_last4: '',
  notes: '',
  id_proof_type: '',
  id_proof_number_last4: ''
};

const AttendanceFormSchema = z.object({
  date_local: z.string().min(1),
  status: z.enum(['present', 'absent', 'half_day', 'leave', 'holiday', 'overtime']),
  hours_worked: z.coerce.number().min(0).max(24).optional(),
  notes: z.string().trim().max(300).optional().or(z.literal(''))
});
type AttendanceFormValues = z.infer<typeof AttendanceFormSchema>;

const MarkPaidFormSchema = z.object({
  paid_via: z.enum(['bank_transfer', 'upi', 'cash', 'cheque']),
  payment_reference: z.string().trim().max(100).optional().or(z.literal(''))
});
type MarkPaidFormValues = z.infer<typeof MarkPaidFormSchema>;

const RunPayrollFormSchema = z.object({
  period_label: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Must be YYYY-MM')
});
type RunPayrollFormValues = z.infer<typeof RunPayrollFormSchema>;

type Tab = 'staff' | 'attendance' | 'payroll';

// URL-synced filter+sort+page state for the Staff tab list. Defaults are
// dropped from the address bar so a fresh /staff URL stays clean.
const STAFF_FILTER_DEFAULTS = {
  search: '',
  status: '',
  category: '',
  employment_type: '',
  sort: 'name_asc',
  page_size: 50
};

// ───── component ─────

export function StaffPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('staff');
  const [terminateTarget, setTerminateTarget] = useState<Staff | null>(null);

  // Filters — staff tab (URL-synced via shared advanced-list toolbar)
  const [filterState, setFilterState] = useUrlSearchState(STAFF_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);

  // Debounce free-text search → URL state to avoid one refetch per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // Filters — attendance/payroll tab
  const [periodFilter, setPeriodFilter] = useState<string>(currentMonthLabel());
  const [attendanceStaffFilter, setAttendanceStaffFilter] = useState<string>(''); // staff_id
  const [payrollStatusFilter, setPayrollStatusFilter] = useState<PayrollStatus | 'all'>('all');

  // Stable hash for pagination reset on filter change (Staff tab only).
  const staffFiltersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.status,
        filterState.category,
        filterState.employment_type,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  // Build server query params from URL state. Empty / default values stripped.
  const staffQueryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      limit: filterState.page_size,
      sort: filterState.sort
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.status) params.status = filterState.status;
    if (filterState.category) params.category = filterState.category;
    if (filterState.employment_type) params.employment_type = filterState.employment_type;
    return params;
  }, [filterState]);

  // ───── staff list ─────
  const [staffCursor, setStaffCursor] = useState<string | null>(null);
  useEffect(() => {
    setStaffCursor(null);
  }, [staffFiltersHash]);
  const staffQuery = useQuery({
    queryKey: ['staff', staffQueryParams, staffCursor],
    queryFn: () =>
      get<{ items: Staff[]; next_cursor: string | null }>('/staff', {
        params: { ...staffQueryParams, ...(staffCursor ? { cursor: staffCursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  const staffPaginator = useCursorAccumulator<Staff>(staffQuery.data, staffCursor, staffFiltersHash);
  const staff = staffPaginator.items;

  // ───── payroll list ─────
  const payrollQuery = useQuery({
    queryKey: ['staff-payroll', periodFilter, payrollStatusFilter],
    queryFn: () =>
      get<{ items: PayrollRow[]; next_cursor: string | null }>('/staff/payroll', {
        params: {
          limit: 100,
          period_label: periodFilter,
          ...(payrollStatusFilter !== 'all' ? { status: payrollStatusFilter } : {})
        }
      }),
    enabled: tab === 'payroll' || tab === 'staff'
  });
  const payroll = payrollQuery.data?.items ?? [];

  // ───── attendance list (per-staff) ─────
  const attendanceQuery = useQuery({
    queryKey: ['staff-attendance', attendanceStaffFilter, periodFilter],
    queryFn: async () => {
      if (!attendanceStaffFilter) return { items: [] as AttendanceRow[], range: { from: null, to: null } };
      // Compute month range from periodFilter
      const [yStr, mStr] = periodFilter.split('-');
      const y = Number(yStr);
      const m = Number(mStr);
      const lastDay = new Date(y, m, 0).getDate();
      const from = `${periodFilter}-01`;
      const to = `${periodFilter}-${String(lastDay).padStart(2, '0')}`;
      return get<{ items: AttendanceRow[]; range: { from: string | null; to: string | null } }>(
        `/staff/${attendanceStaffFilter}/attendance`,
        { params: { from, to } }
      );
    },
    enabled: tab === 'attendance' && attendanceStaffFilter.length > 0
  });
  const attendanceRows = attendanceQuery.data?.items ?? [];

  // ───── KPIs ─────
  const kpiActive = staff.filter((s) => s.status === 'active').length;
  const kpiOnLeave = staff.filter((s) => s.status === 'on_leave').length;
  const kpiMonthlyWageBill = staff
    .filter((s) => s.status === 'active' || s.status === 'on_leave')
    .reduce((sum, s) => sum + s.monthly_salary_inr, 0);
  const kpiUnpaidPayrolls = payroll.filter((p) => p.status === 'pending').length;

  // ───── staff form ─────
  const [editing, setEditing] = useState<Staff | null>(null);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [idProofFile, setIdProofFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const staffForm = useForm<StaffFormValues>({
    resolver: zodResolver(StaffFormSchema),
    defaultValues: DEFAULT_STAFF_FORM,
    mode: 'onChange'
  });

  const resetUploadState = (): void => {
    setPhotoFile(null);
    setIdProofFile(null);
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl(null);
    }
  };

  const onPhotoFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && !STAFF_IMAGE_MIME.includes(f.type)) {
      toast.error('Photo must be JPEG, PNG or WebP');
      e.target.value = '';
      return;
    }
    if (photoPreviewUrl) URL.revokeObjectURL(photoPreviewUrl);
    setPhotoFile(f);
    setPhotoPreviewUrl(f ? URL.createObjectURL(f) : null);
  };

  const onIdProofFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const f = e.target.files?.[0] ?? null;
    if (f && !STAFF_IMAGE_MIME.includes(f.type)) {
      toast.error('ID proof must be JPEG, PNG or WebP');
      e.target.value = '';
      return;
    }
    setIdProofFile(f);
  };

  const openCreate = (): void => {
    setEditing(null);
    resetUploadState();
    staffForm.reset(DEFAULT_STAFF_FORM);
    setStaffDialogOpen(true);
  };
  const openEdit = (s: Staff): void => {
    setEditing(s);
    resetUploadState();
    staffForm.reset({
      full_name: s.full_name,
      phone: s.phone,
      email: s.email ?? '',
      category: s.category,
      employment_type: s.employment_type,
      monthly_salary_inr: s.monthly_salary_inr,
      joining_date: s.joining_date.slice(0, 10),
      emergency_contact_name: s.emergency_contact_name ?? '',
      emergency_contact_phone: s.emergency_contact_phone ?? '',
      address: s.address ?? '',
      bank_account_last4: s.bank_account_last4 ?? '',
      pan_last4: s.pan_last4 ?? '',
      notes: s.notes ?? '',
      id_proof_type: s.id_proof_type ?? '',
      id_proof_number_last4: s.id_proof_number_last4 ?? ''
    });
    setStaffDialogOpen(true);
  };

  const saveStaff = useMutation({
    mutationFn: async (v: StaffFormValues) => {
      // ID-proof metadata is recorded with the file upload itself when a file
      // is supplied; on metadata-only edits we send it in the row body.
      const body: Record<string, unknown> = {
        full_name: v.full_name,
        phone: v.phone,
        email: v.email?.trim() || undefined,
        category: v.category,
        employment_type: v.employment_type,
        monthly_salary_inr: v.monthly_salary_inr,
        joining_date: v.joining_date,
        emergency_contact_name: v.emergency_contact_name?.trim() || undefined,
        emergency_contact_phone: v.emergency_contact_phone?.trim() || undefined,
        address: v.address?.trim() || undefined,
        bank_account_last4: v.bank_account_last4?.trim() || undefined,
        pan_last4: v.pan_last4?.trim() || undefined,
        notes: v.notes?.trim() || undefined
      };
      if (!idProofFile) {
        if (v.id_proof_type) body.id_proof_type = v.id_proof_type;
        if (v.id_proof_number_last4?.trim()) {
          body.id_proof_number_last4 = v.id_proof_number_last4.trim();
        }
      }
      const saved = editing
        ? await patch<Staff>(`/staff/${editing.id}`, body)
        : await post<Staff>('/staff', body);

      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/staff/${saved.id}/photo`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      if (idProofFile) {
        if (!v.id_proof_type) throw new Error('Select an ID proof type before uploading');
        const fd = new FormData();
        fd.append('file', idProofFile);
        fd.append('id_proof_type', v.id_proof_type);
        if (v.id_proof_number_last4?.trim()) {
          fd.append('id_proof_number_last4', v.id_proof_number_last4.trim());
        }
        await api.post(`/staff/${saved.id}/id-proof`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      return saved;
    },
    onSuccess: () => {
      toast.success(editing ? 'Staff updated' : 'Staff created');
      setStaffDialogOpen(false);
      resetUploadState();
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const terminateStaff = useMutation({
    mutationFn: (id: string) => del<Staff>(`/staff/${id}`),
    onSuccess: () => {
      toast.success('Staff terminated');
      void queryClient.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── attendance form ─────
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [attendanceForStaff, setAttendanceForStaff] = useState<Staff | null>(null);
  const attendanceForm = useForm<AttendanceFormValues>({
    resolver: zodResolver(AttendanceFormSchema),
    defaultValues: { date_local: todayIsoDate(), status: 'present', hours_worked: undefined, notes: '' },
    mode: 'onChange'
  });

  const openAttendanceDialog = (s: Staff, dateLocal?: string): void => {
    setAttendanceForStaff(s);
    attendanceForm.reset({
      date_local: dateLocal ?? todayIsoDate(),
      status: 'present',
      hours_worked: undefined,
      notes: ''
    });
    setAttendanceDialogOpen(true);
  };

  const saveAttendance = useMutation({
    mutationFn: (v: AttendanceFormValues) => {
      if (!attendanceForStaff) throw new Error('No staff selected');
      const body = {
        date_local: v.date_local,
        status: v.status,
        hours_worked: v.hours_worked === undefined || Number.isNaN(v.hours_worked) ? undefined : v.hours_worked,
        notes: v.notes?.trim() || undefined
      };
      return post<AttendanceRow>(`/staff/${attendanceForStaff.id}/attendance`, body);
    },
    onSuccess: () => {
      toast.success('Attendance marked');
      setAttendanceDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['staff-attendance'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── bulk-mark today present ─────
  const bulkMarkToday = useMutation({
    mutationFn: async () => {
      const date = todayIsoDate();
      const actives = staff.filter((s) => s.status === 'active');
      let marked = 0;
      for (const s of actives) {
        try {
          await post<AttendanceRow>(`/staff/${s.id}/attendance`, { date_local: date, status: 'present' });
          marked += 1;
        } catch {
          // ignore individual failures (already marked etc.)
        }
      }
      return { marked, total: actives.length };
    },
    onSuccess: (r) => {
      toast.success(`Marked present for ${r.marked} / ${r.total} active staff`);
      void queryClient.invalidateQueries({ queryKey: ['staff-attendance'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── payroll: run-month ─────
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const runForm = useForm<RunPayrollFormValues>({
    resolver: zodResolver(RunPayrollFormSchema),
    defaultValues: { period_label: currentMonthLabel() },
    mode: 'onChange'
  });

  const runMonth = useMutation({
    mutationFn: (v: RunPayrollFormValues) =>
      post<{ period_label: string; generated: number; skipped: number; total_active: number }>(
        '/staff/payroll/run-month',
        { period_label: v.period_label }
      ),
    onSuccess: (r) => {
      toast.success(`Payroll: ${r.generated} generated, ${r.skipped} skipped`);
      setRunDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['staff-payroll'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── payroll: mark-paid ─────
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [payrollForMarkPaid, setPayrollForMarkPaid] = useState<PayrollRow | null>(null);
  const markPaidForm = useForm<MarkPaidFormValues>({
    resolver: zodResolver(MarkPaidFormSchema),
    defaultValues: { paid_via: 'bank_transfer', payment_reference: '' },
    mode: 'onChange'
  });

  const openMarkPaidDialog = (p: PayrollRow): void => {
    setPayrollForMarkPaid(p);
    markPaidForm.reset({ paid_via: 'bank_transfer', payment_reference: '' });
    setMarkPaidDialogOpen(true);
  };

  const markPaidMutation = useMutation({
    mutationFn: (v: MarkPaidFormValues) => {
      if (!payrollForMarkPaid) throw new Error('No payroll selected');
      return post<PayrollRow>(`/staff/payroll/${payrollForMarkPaid.id}/mark-paid`, {
        paid_via: v.paid_via,
        payment_reference: v.payment_reference?.trim() || undefined
      });
    },
    onSuccess: () => {
      toast.success('Payroll marked paid');
      setMarkPaidDialogOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['staff-payroll'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── payroll: single-staff generate ─────
  const generateForStaff = useMutation({
    mutationFn: (staffId: string) =>
      post<PayrollRow>(`/staff/${staffId}/payroll/generate`, { period_label: periodFilter }),
    onSuccess: () => {
      toast.success('Payroll generated');
      void queryClient.invalidateQueries({ queryKey: ['staff-payroll'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ───── CSV export helpers ─────
  const [exporting, setExporting] = useState(false);

  const downloadCsv = async (urlPath: string, params: Record<string, string>, fallbackName: string): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4100';
      const apiPrefix = import.meta.env.VITE_API_PREFIX ?? '/v1';
      const qs = new URLSearchParams(params).toString();
      const url = `${apiUrl}${apiPrefix}${urlPath}${qs ? '?' + qs : ''}`;
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include'
      });
      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const body = (await res.json()) as { error?: { message?: string } };
          if (body?.error?.message) msg = body.error.message;
        } catch {
          /* ignore */
        }
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m?.[1] ?? fallbackName;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success('Export downloaded');
    } catch (e) {
      toast.error(getErrorMessage(e));
    } finally {
      setExporting(false);
    }
  };

  // ───── kebab ─────
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const toggleMenu = (id: string): void => setOpenMenuId((cur) => (cur === id ? null : id));

  // ───── attendance summary for selected staff (counts) ─────
  const attendanceCounts = useMemo(() => {
    const counts = { present: 0, absent: 0, half_day: 0, leave: 0, holiday: 0, overtime: 0 };
    for (const r of attendanceRows) {
      counts[r.status] += 1;
    }
    return counts;
  }, [attendanceRows]);

  // ───── shared toolbar wiring (Staff tab only) ─────
  const staffSortOptions = useMemo(
    () => [
      { value: 'name_asc', label: 'Name (A–Z)' },
      { value: 'name_desc', label: 'Name (Z–A)' },
      { value: 'joining_date_desc', label: 'Joining date (newest)' },
      { value: 'salary_desc', label: 'Salary (high → low)' }
    ],
    []
  );

  const staffFilterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'status',
        label: 'Status',
        type: 'select',
        value: filterState.status,
        options: [
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'on_leave', label: 'On leave' },
          { value: 'inactive', label: 'Inactive' },
          { value: 'terminated', label: 'Terminated' }
        ],
        onChange: (v) => setFilterState({ status: v })
      },
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        value: filterState.category,
        options: [
          { value: '', label: 'All' },
          ...CATEGORY_OPTIONS.map((c) => ({ value: c, label: CATEGORY_LABEL[c] }))
        ],
        onChange: (v) => setFilterState({ category: v })
      },
      {
        id: 'employment_type',
        label: 'Employment',
        type: 'select',
        value: filterState.employment_type,
        options: [
          { value: '', label: 'All' },
          { value: 'full_time', label: 'Full-time' },
          { value: 'part_time', label: 'Part-time' },
          { value: 'contract', label: 'Contract' }
        ],
        onChange: (v) => setFilterState({ employment_type: v })
      }
    ],
    [filterState, setFilterState]
  );

  const staffHasActiveFilters =
    filterState.search !== STAFF_FILTER_DEFAULTS.search ||
    filterState.status !== STAFF_FILTER_DEFAULTS.status ||
    filterState.category !== STAFF_FILTER_DEFAULTS.category ||
    filterState.employment_type !== STAFF_FILTER_DEFAULTS.employment_type ||
    filterState.sort !== STAFF_FILTER_DEFAULTS.sort ||
    filterState.page_size !== STAFF_FILTER_DEFAULTS.page_size;

  const resetStaffFilters = (): void => {
    setFilterState(STAFF_FILTER_DEFAULTS);
    setSearchInput('');
  };

  // ───── render dialogs ─────

  const staffFormDialog = (
    <Dialog open={staffDialogOpen} onOpenChange={setStaffDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit staff' : 'Add staff'}</DialogTitle>
          <DialogDescription>
            Direct society employee on payroll. Distinct from contracted vendors.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={staffForm.handleSubmit((v) => saveStaff.mutate(v))} className="flex flex-col gap-vt-3">
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-name">Full name</Label>
              <Input id="s-name" placeholder="Ramesh Sweeper" {...staffForm.register('full_name')} />
              {staffForm.formState.errors.full_name ? (
                <p className="text-vt-12 text-vt-alert">{staffForm.formState.errors.full_name.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-phone">Phone</Label>
              <Input id="s-phone" placeholder="+91…" {...staffForm.register('phone')} />
              {staffForm.formState.errors.phone ? (
                <p className="text-vt-12 text-vt-alert">{staffForm.formState.errors.phone.message}</p>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-email">Email</Label>
              <Input id="s-email" type="email" placeholder="staff@example.com" {...staffForm.register('email')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-category">Category</Label>
              <Select id="s-category" {...staffForm.register('category')}>
                {CATEGORY_OPTIONS.map((c) => (
                  <option key={c} value={c}>
                    {CATEGORY_LABEL[c]}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-emp">Employment type</Label>
              <Select id="s-emp" {...staffForm.register('employment_type')}>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contract">Contract</option>
              </Select>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-salary">Monthly salary (₹)</Label>
              <Input id="s-salary" type="number" min={0} {...staffForm.register('monthly_salary_inr')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-join">Joining date</Label>
              <Input id="s-join" type="date" {...staffForm.register('joining_date')} />
              {staffForm.formState.errors.joining_date ? (
                <p className="text-vt-12 text-vt-alert">{staffForm.formState.errors.joining_date.message}</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-emer-name">Emergency contact name</Label>
              <Input id="s-emer-name" placeholder="Sita Devi" {...staffForm.register('emergency_contact_name')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-emer-phone">Emergency phone</Label>
              <Input id="s-emer-phone" placeholder="+91…" {...staffForm.register('emergency_contact_phone')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-address">Address</Label>
              <Input id="s-address" {...staffForm.register('address')} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-bank">Bank account (last 4)</Label>
              <Input id="s-bank" placeholder="1234" {...staffForm.register('bank_account_last4')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="s-pan">PAN (last 4)</Label>
              <Input id="s-pan" placeholder="9X8Y" {...staffForm.register('pan_last4')} />
            </div>
          </div>

          {/* Photo + ID proof */}
          <div className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3">
            <div className="mb-vt-2 text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
              Profile photo &amp; ID proof
            </div>
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="s-photo">Profile photo</Label>
                <div className="flex items-start gap-vt-3">
                  {/*
                    Passport-style preview (140×170, rounded-vt-md, vt-border).
                    Small list avatars stay 32px circular in the table — only
                    detail / edit views grow to passport size.
                  */}
                  {photoPreviewUrl ? (
                    <img
                      src={photoPreviewUrl}
                      alt="Selected photo preview"
                      className="shrink-0 rounded-vt-md border border-vt-border object-cover"
                      style={{ width: 140, height: 170 }}
                    />
                  ) : editing ? (
                    <StaffAvatar staff={editing} size={140} variant="passport" />
                  ) : (
                    <span
                      className="flex shrink-0 items-center justify-center rounded-vt-md border border-vt-border bg-vt-primary-100 text-vt-24 font-semibold text-vt-primary"
                      style={{ width: 140, height: 170 }}
                    >
                      ?
                    </span>
                  )}
                  <Input
                    id="s-photo"
                    type="file"
                    accept={STAFF_IMAGE_ACCEPT}
                    onChange={onPhotoFileChange}
                  />
                </div>
                <p className="text-vt-11 text-vt-text-muted">JPEG / PNG / WebP, ≤ 10 MB. Passport-style (3.5:4.5) preferred.</p>
              </div>

              <div className="flex flex-col gap-vt-2">
                <Label htmlFor="s-idproof-type">ID proof type</Label>
                <Select id="s-idproof-type" {...staffForm.register('id_proof_type')}>
                  <option value="">— None —</option>
                  {ID_PROOF_OPTIONS.map((t) => (
                    <option key={t} value={t}>
                      {ID_PROOF_LABEL[t]}
                    </option>
                  ))}
                </Select>
                <Label htmlFor="s-idproof-last4">ID number (last 4)</Label>
                <Input
                  id="s-idproof-last4"
                  placeholder="1234"
                  maxLength={10}
                  {...staffForm.register('id_proof_number_last4')}
                />
                <Label htmlFor="s-idproof-file">ID proof scan</Label>
                <Input
                  id="s-idproof-file"
                  type="file"
                  accept={STAFF_IMAGE_ACCEPT}
                  onChange={onIdProofFileChange}
                />
                {editing?.id_proof_storage_key && !idProofFile ? (
                  <p className="text-vt-11 text-vt-text-muted">
                    Existing scan on file{editing.id_proof_type ? ` (${ID_PROOF_LABEL[editing.id_proof_type]})` : ''}.
                    Upload to replace.
                  </p>
                ) : (
                  <p className="text-vt-11 text-vt-text-muted">
                    Full ID number is never stored — only the last 4 digits and a scan.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="s-notes">Notes</Label>
            <Textarea id="s-notes" rows={2} {...staffForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setStaffDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveStaff.isPending}
              loadingText="Saving…"
              disabled={saveStaff.isPending || !staffForm.formState.isValid}
            >
              {editing ? 'Save changes' : 'Create staff'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const attendanceFormDialog = (
    <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Mark attendance{attendanceForStaff ? ` — ${attendanceForStaff.full_name}` : ''}
          </DialogTitle>
          <DialogDescription>Daily status, optional hours worked, optional note.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={attendanceForm.handleSubmit((v) => saveAttendance.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-date">Date</Label>
              <Input id="a-date" type="date" {...attendanceForm.register('date_local')} />
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="a-status">Status</Label>
              <Select id="a-status" {...attendanceForm.register('status')}>
                <option value="present">Present</option>
                <option value="absent">Absent</option>
                <option value="half_day">Half day</option>
                <option value="leave">Leave</option>
                <option value="holiday">Holiday</option>
                <option value="overtime">Overtime</option>
              </Select>
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="a-hours">Hours worked (optional)</Label>
            <Input id="a-hours" type="number" min={0} max={24} step="0.5" {...attendanceForm.register('hours_worked')} />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="a-notes">Notes</Label>
            <Textarea id="a-notes" rows={2} {...attendanceForm.register('notes')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setAttendanceDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={saveAttendance.isPending}
              loadingText="Saving…"
              disabled={saveAttendance.isPending || !attendanceForm.formState.isValid}
            >
              Save attendance
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const runPayrollDialog = (
    <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run payroll</DialogTitle>
          <DialogDescription>
            Generates pending payroll records for every active staff member. Already-generated rows are skipped.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={runForm.handleSubmit((v) => runMonth.mutate(v))} className="flex flex-col gap-vt-3">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="r-period">Period (YYYY-MM)</Label>
            <Input id="r-period" placeholder="2026-06" {...runForm.register('period_label')} />
            {runForm.formState.errors.period_label ? (
              <p className="text-vt-12 text-vt-alert">{runForm.formState.errors.period_label.message}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setRunDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={runMonth.isPending}
              loadingText="Running…"
              disabled={runMonth.isPending || !runForm.formState.isValid}
            >
              Run payroll
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const markPaidDialog = (
    <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mark payroll paid</DialogTitle>
          <DialogDescription>
            {payrollForMarkPaid
              ? `${payrollForMarkPaid.staff?.full_name ?? 'Staff'} · ${payrollForMarkPaid.period_label} · ${formatInr(payrollForMarkPaid.net_pay_inr)}`
              : 'Confirm payment.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={markPaidForm.handleSubmit((v) => markPaidMutation.mutate(v))}
          className="flex flex-col gap-vt-3"
        >
          <div className="flex flex-col gap-vt-2">
            <Label>Paid via</Label>
            <div className="grid grid-cols-2 gap-vt-2">
              {(['bank_transfer', 'upi', 'cash', 'cheque'] as PaidVia[]).map((m) => (
                <label
                  key={m}
                  className="flex cursor-pointer items-center gap-vt-2 rounded-vt-sm border border-vt-border bg-vt-surface-2 px-vt-3 py-vt-2 text-vt-13 text-vt-text has-[:checked]:border-vt-primary has-[:checked]:bg-vt-primary-100"
                >
                  <input type="radio" value={m} {...markPaidForm.register('paid_via')} />
                  <span>{m === 'bank_transfer' ? 'Bank transfer' : m === 'upi' ? 'UPI' : m === 'cash' ? 'Cash' : 'Cheque'}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="p-ref">Payment reference (optional)</Label>
            <Input id="p-ref" placeholder="UTR / cheque no / txn id" {...markPaidForm.register('payment_reference')} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setMarkPaidDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={markPaidMutation.isPending}
              loadingText="Saving…"
              disabled={markPaidMutation.isPending || !markPaidForm.formState.isValid}
            >
              Mark paid
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  // ───── render ─────

  return (
    <>
      <PageHeader
        title="Staff"
        description="Direct society employees — payroll, daily attendance, monthly settlement."
        actions={
          <div className="flex items-center gap-vt-2">
            <Button size="sm" variant="secondary" onClick={() => setRunDialogOpen(true)}>
              <PlayCircle className="h-4 w-4" /> Run payroll
            </Button>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4" /> Add staff
            </Button>
          </div>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-2 xl:grid-cols-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <UsersIcon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Active staff</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {staffQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiActive}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-warn-100)] text-vt-warn">
                <Coffee className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">On leave</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {staffQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiOnLeave}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-info-100)] text-vt-info">
                <IndianRupee className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Monthly wage bill</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {staffQuery.isLoading ? <Skeleton className="h-7 w-20" /> : formatInr(kpiMonthlyWageBill)}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-alert-100)] text-vt-alert">
                <ReceiptIcon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Unpaid payrolls ({periodFilter})</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {payrollQuery.isLoading ? <Skeleton className="h-7 w-12" /> : kpiUnpaidPayrolls}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardContent className="flex items-center gap-vt-2 p-vt-3">
            <TabsPills
              value={tab}
              onChange={(v) => setTab(v as Tab)}
              options={[
                { value: 'staff', label: 'Staff' },
                { value: 'attendance', label: 'Attendance' },
                { value: 'payroll', label: 'Payroll' }
              ]}
              aria-label="Staff sections"
            />
          </CardContent>
        </Card>

        {/* ───── STAFF TAB ───── */}
        {tab === 'staff' ? (
          <>
            <AdvancedListToolbar
              searchValue={searchInput}
              onSearchChange={setSearchInput}
              searchPlaceholder="Search by name, phone, or email…"
              filters={staffFilterChips}
              sort={{
                value: filterState.sort,
                onChange: (v) => setFilterState({ sort: v }),
                options: staffSortOptions
              }}
              pageSize={filterState.page_size}
              onPageSizeChange={(n) => setFilterState({ page_size: n })}
              onReset={resetStaffFilters}
              hasActiveFilters={staffHasActiveFilters}
              resultLabel={
                staffQuery.data
                  ? `${staff.length} staff member${staff.length === 1 ? '' : 's'}`
                  : 'Loading…'
              }
            />

            <Card>
              <CardContent className="p-vt-4">
                {staffQuery.isLoading ? (
                  <div className="flex flex-col gap-vt-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : staffQuery.isError ? (
                  <EmptyState
                    icon={<HardHat className="h-6 w-6" />}
                    title="Could not load staff"
                    description={getErrorMessage(staffQuery.error)}
                    action={
                      <Button variant="secondary" onClick={() => void staffQuery.refetch()}>
                        Try again
                      </Button>
                    }
                  />
                ) : staff.length === 0 ? (
                  staffHasActiveFilters ? (
                    <EmptyState
                      icon={<HardHat className="h-6 w-6" />}
                      title="No staff match your filters"
                      description="Try widening the filters or clear them to see the full roster."
                      action={
                        <Button variant="secondary" onClick={resetStaffFilters}>
                          Reset filters
                        </Button>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<HardHat className="h-6 w-6" />}
                      title="No staff yet"
                      description="Add direct society employees — housekeeping, gardening, security assistants — with payroll baseline."
                      action={
                        <Button onClick={openCreate}>
                          <Plus className="h-4 w-4" /> Add staff
                        </Button>
                      }
                    />
                  )
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Salary / month</TableHead>
                        <TableHead>Joined</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staff.map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div className="flex items-center gap-vt-3">
                              <StaffAvatar staff={s} size={32} />
                              <div className="min-w-0">
                                <div className="font-medium text-vt-text">{s.full_name}</div>
                                <div className="text-vt-12 text-vt-text-muted">{s.email ?? ''}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-vt-text-muted">
                            <span className="inline-flex items-center gap-1 text-vt-13">
                              <Phone className="h-3.5 w-3.5" />
                              {s.phone}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="muted">{CATEGORY_LABEL[s.category]}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="info">{EMPLOYMENT_LABEL[s.employment_type]}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                            {formatInr(s.monthly_salary_inr)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                            {formatDate(s.joining_date)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="relative inline-block">
                              <Button size="sm" variant="ghost" title="More actions" onClick={() => toggleMenu(s.id)}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                              {openMenuId === s.id ? (
                                <div
                                  className="absolute right-0 z-10 mt-1 w-52 overflow-hidden rounded-vt-md border border-vt-border bg-vt-surface shadow-vt-2"
                                  onMouseLeave={() => setOpenMenuId(null)}
                                >
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      openEdit(s);
                                    }}
                                  >
                                    <Edit3 className="h-4 w-4" /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      openAttendanceDialog(s);
                                    }}
                                  >
                                    <CheckCircle2 className="h-4 w-4" /> Mark attendance today
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      setAttendanceStaffFilter(s.id);
                                      setTab('attendance');
                                    }}
                                  >
                                    <CalendarDays className="h-4 w-4" /> View attendance
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-text hover:bg-vt-surface-2"
                                    onClick={() => {
                                      setOpenMenuId(null);
                                      generateForStaff.mutate(s.id);
                                    }}
                                  >
                                    <ReceiptIcon className="h-4 w-4" /> Generate payroll ({periodFilter})
                                  </button>
                                  {s.status !== 'terminated' ? (
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-vt-2 px-vt-3 py-vt-2 text-left text-vt-13 text-vt-alert hover:bg-vt-surface-2"
                                      onClick={() => {
                                        setOpenMenuId(null);
                                        setTerminateTarget(s);
                                      }}
                                    >
                                      <Power className="h-4 w-4" /> Terminate
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <CursorPaginator
                  hasMore={Boolean(staffPaginator.nextCursor)}
                  loading={staffQuery.isFetching}
                  itemsShown={staff.length}
                  pageSize={filterState.page_size}
                  onLoadMore={() => {
                    if (staffPaginator.nextCursor) setStaffCursor(staffPaginator.nextCursor);
                  }}
                />
              </CardContent>
            </Card>
          </>
        ) : null}

        {/* ───── ATTENDANCE TAB ───── */}
        {tab === 'attendance' ? (
          <>
            <Card>
              <CardContent className="flex flex-wrap items-center gap-vt-3 p-vt-4">
                <Filter className="h-4 w-4 text-vt-text-muted" />
                <Label htmlFor="att-month" className="text-vt-12 text-vt-text-muted">
                  Month:
                </Label>
                <Input
                  id="att-month"
                  type="month"
                  className="h-8 w-36 text-vt-12"
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                />
                <Label htmlFor="att-staff" className="text-vt-12 text-vt-text-muted">
                  Staff:
                </Label>
                <Select
                  id="att-staff"
                  className="h-8 w-56 text-vt-12"
                  value={attendanceStaffFilter}
                  onChange={(e) => setAttendanceStaffFilter(e.target.value)}
                >
                  <option value="">— Select staff —</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.full_name} · {CATEGORY_LABEL[s.category]}
                    </option>
                  ))}
                </Select>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => bulkMarkToday.mutate()}
                  disabled={bulkMarkToday.isPending || staff.length === 0}
                >
                  <CheckCircle2 className="h-4 w-4" /> Mark today for all active
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const [yStr, mStr] = periodFilter.split('-');
                    const y = Number(yStr);
                    const m = Number(mStr);
                    const lastDay = new Date(y, m, 0).getDate();
                    const from = `${periodFilter}-01`;
                    const to = `${periodFilter}-${String(lastDay).padStart(2, '0')}`;
                    void downloadCsv(
                      '/staff/attendance/export.csv',
                      {
                        from,
                        to,
                        ...(attendanceStaffFilter ? { staff_id: attendanceStaffFilter } : {})
                      },
                      `hooddesk-staff-attendance-${periodFilter}.csv`
                    );
                  }}
                  disabled={exporting}
                >
                  <Download className="h-4 w-4" /> Export CSV
                </Button>
              </CardContent>
            </Card>

            {attendanceStaffFilter === '' ? (
              <Card>
                <CardContent className="p-vt-4">
                  <EmptyState
                    icon={<CalendarDays className="h-6 w-6" />}
                    title="Select a staff member"
                    description="Pick a staff above to see and mark their monthly attendance grid."
                  />
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Counts strip */}
                <Card>
                  <CardContent className="grid grid-cols-2 gap-vt-3 p-vt-4 md:grid-cols-6">
                    {(
                      [
                        ['present', 'Present'],
                        ['overtime', 'Overtime'],
                        ['half_day', 'Half day'],
                        ['leave', 'Leave'],
                        ['holiday', 'Holiday'],
                        ['absent', 'Absent']
                      ] as Array<[keyof typeof attendanceCounts, string]>
                    ).map(([k, l]) => (
                      <div key={k} className="rounded-vt-sm bg-vt-surface-2 p-vt-3">
                        <div className="text-vt-12 text-vt-text-muted">{l}</div>
                        <div className="text-vt-18 font-semibold text-vt-text">{attendanceCounts[k]}</div>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-vt-4">
                    {attendanceQuery.isLoading ? (
                      <div className="flex flex-col gap-vt-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton key={i} className="h-10 w-full" />
                        ))}
                      </div>
                    ) : attendanceRows.length === 0 ? (
                      <EmptyState
                        icon={<CalendarDays className="h-6 w-6" />}
                        title="No attendance for this month"
                        description="Mark today's status to start the log."
                        action={
                          <Button
                            onClick={() => {
                              const s = staff.find((x) => x.id === attendanceStaffFilter);
                              if (s) openAttendanceDialog(s);
                            }}
                          >
                            <Plus className="h-4 w-4" /> Mark attendance
                          </Button>
                        }
                      />
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Check-in</TableHead>
                            <TableHead>Check-out</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {attendanceRows.map((r) => (
                            <TableRow key={r.id}>
                              <TableCell className="whitespace-nowrap text-vt-13 text-vt-text">
                                {formatDate(r.date_local)}
                              </TableCell>
                              <TableCell>
                                <Badge variant={ATTENDANCE_VARIANT[r.status]}>{ATTENDANCE_LABEL[r.status]}</Badge>
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                                {r.check_in_at ? formatDateTime(r.check_in_at) : '—'}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-vt-text-muted text-vt-12">
                                {r.check_out_at ? formatDateTime(r.check_out_at) : '—'}
                              </TableCell>
                              <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                                {r.hours_worked ?? '—'}
                              </TableCell>
                              <TableCell className="text-vt-12 text-vt-text-muted">{r.notes ?? '—'}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const s = staff.find((x) => x.id === attendanceStaffFilter);
                                    if (s) openAttendanceDialog(s, r.date_local);
                                  }}
                                >
                                  <Edit3 className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </>
        ) : null}

        {/* ───── PAYROLL TAB ───── */}
        {tab === 'payroll' ? (
          <>
            <Card>
              <CardContent className="flex flex-wrap items-center gap-vt-3 p-vt-4">
                <Filter className="h-4 w-4 text-vt-text-muted" />
                <Label htmlFor="pay-month" className="text-vt-12 text-vt-text-muted">
                  Month:
                </Label>
                <Input
                  id="pay-month"
                  type="month"
                  className="h-8 w-36 text-vt-12"
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                />
                <span className="text-vt-12 text-vt-text-muted">Status:</span>
                <TabsPills
                  size="sm"
                  value={payrollStatusFilter}
                  onChange={(v) => setPayrollStatusFilter(v as 'all' | PayrollStatus)}
                  options={(['all', 'pending', 'paid', 'cancelled'] as Array<'all' | PayrollStatus>).map((s) => ({
                    value: s,
                    label: s === 'all' ? 'All' : PAYROLL_STATUS_LABEL[s as PayrollStatus]
                  }))}
                  aria-label="Payroll status filter"
                />
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    downloadCsv(
                      '/staff/payroll/export.csv',
                      { period_label: periodFilter },
                      `hooddesk-staff-payroll-${periodFilter}.csv`
                    )
                  }
                  disabled={exporting}
                >
                  <Download className="h-4 w-4" /> Export CSV
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-vt-4">
                {payrollQuery.isLoading ? (
                  <div className="flex flex-col gap-vt-2">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : payrollQuery.isError ? (
                  <EmptyState
                    icon={<ReceiptIcon className="h-6 w-6" />}
                    title="Could not load payroll"
                    description={getErrorMessage(payrollQuery.error)}
                    action={
                      <Button variant="secondary" onClick={() => void payrollQuery.refetch()}>
                        Try again
                      </Button>
                    }
                  />
                ) : payroll.length === 0 ? (
                  <EmptyState
                    icon={<ReceiptIcon className="h-6 w-6" />}
                    title="No payroll for this period"
                    description={`Click "Run payroll" to generate ${periodFilter} payroll for all active staff.`}
                    action={
                      <Button onClick={() => setRunDialogOpen(true)}>
                        <PlayCircle className="h-4 w-4" /> Run payroll
                      </Button>
                    }
                  />
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Staff</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Base</TableHead>
                        <TableHead className="text-right">Days worked / total</TableHead>
                        <TableHead className="text-right">Deduct</TableHead>
                        <TableHead className="text-right">Bonus</TableHead>
                        <TableHead className="text-right">Net pay</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Paid at / via</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payroll.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>
                            <div className="font-medium text-vt-text">{p.staff?.full_name ?? '—'}</div>
                            <div className="text-vt-12 text-vt-text-muted">
                              {p.staff ? CATEGORY_LABEL[p.staff.category] : ''}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-vt-13 text-vt-text">{p.period_label}</TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-vt-text">
                            {formatInr(p.base_salary_inr)}
                          </TableCell>
                          <TableCell className="text-right text-vt-13 text-vt-text-muted">
                            {p.attendance_days_worked} / {p.attendance_total_days}
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-vt-text-muted">
                            {formatInr(p.deductions_inr)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 text-vt-text-muted">
                            {formatInr(p.bonus_inr)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-vt-13 font-semibold text-vt-text">
                            {formatInr(p.net_pay_inr)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={PAYROLL_STATUS_VARIANT[p.status]}>{PAYROLL_STATUS_LABEL[p.status]}</Badge>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-vt-12 text-vt-text-muted">
                            {p.paid_at ? (
                              <>
                                {formatDateTime(p.paid_at)}
                                {p.paid_via ? <div>{p.paid_via.replace('_', ' ')}</div> : null}
                              </>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {p.status === 'pending' ? (
                              <Button size="sm" variant="secondary" onClick={() => openMarkPaidDialog(p)}>
                                <CheckCircle2 className="h-4 w-4" /> Mark paid
                              </Button>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </div>

      {staffFormDialog}
      {attendanceFormDialog}
      {runPayrollDialog}
      {markPaidDialog}

      <ConfirmDialog
        open={Boolean(terminateTarget)}
        onOpenChange={(v) => (v ? null : setTerminateTarget(null))}
        title="Terminate staff"
        description={
          terminateTarget ? (
            <>
              Terminate <strong>{terminateTarget.full_name}</strong>? They are removed from the
              active roster and payroll for future periods. Past attendance and payroll records
              are retained.
            </>
          ) : null
        }
        confirmLabel="Terminate"
        variant="danger"
        loading={terminateStaff.isPending}
        onConfirm={() => {
          if (!terminateTarget) return;
          terminateStaff.mutate(terminateTarget.id, { onSuccess: () => setTerminateTarget(null) });
        }}
      />
    </>
  );
}
